import {
  Button,
  Card,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, SearchOutlined, UserAddOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { hrApi, type StaffSummary } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { useHrLookups } from './lookups';
import { CreateUserFromStaffModal } from './create-user-from-staff-modal';

export function HrStaffListPage() {
  const navigate = useNavigate();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canCreateUser = useAuthStore((s) => s.hasAuthority('USER_MGMT:CREATE'));
  const lookups = useHrLookups();
  const [createUserFor, setCreateUserFor] = useState<StaffSummary | null>(null);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  const [designationId, setDesignationId] = useState<number | undefined>();
  const [employmentTypeId, setEmploymentTypeId] = useState<number | undefined>();
  const [active, setActive] = useState<'all' | 'active' | 'inactive'>('active');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debounced, departmentId, designationId, employmentTypeId, active]);

  const params = {
    q: debounced || undefined,
    departmentId,
    designationId,
    employmentTypeId,
    active: active === 'all' ? undefined : active === 'active',
    page,
    size,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['hr', 'staff', 'search', params],
    queryFn: () => hrApi.searchStaff(params),
    placeholderData: (prev) => prev,
  });

  const columns: TableProps<StaffSummary>['columns'] = [
    {
      title: 'Name',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.firstName} {row.lastName}</Typography.Text>
          {row.cnic && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              CNIC {row.cnic}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Designation',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <span>{row.designationName ?? <Typography.Text type="secondary">—</Typography.Text>}</span>
          {row.departmentName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>{row.departmentName}</Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'employmentTypeName',
      width: 120,
      render: (n: string | null) => (n ? <Tag>{n}</Tag> : '—'),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      width: 140,
      render: (p: string | null) => p || '—',
    },
    {
      title: 'Joined',
      dataIndex: 'joiningDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      width: 110,
      render: (a: boolean, row) =>
        a ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Space orientation="vertical" size={0}>
            <Tag>Left</Tag>
            {row.leavingDate && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {dayjs(row.leavingDate).format('D MMM YYYY')}
              </Typography.Text>
            )}
          </Space>
        ),
    },
    {
      title: 'User account',
      width: 160,
      render: (_, row) => {
        if (row.linkedUsername) {
          return (
            <Tooltip title={row.linkedUserActive === false ? 'User is deactivated' : 'Login user'}>
              <Tag color={row.linkedUserActive === false ? 'default' : 'blue'}>
                {row.linkedUsername}
              </Tag>
            </Tooltip>
          );
        }
        if (!canCreateUser) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        return (
          <Button
            size="small"
            icon={<UserAddOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setCreateUserFor(row);
            }}
          >
            Create user
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module"
        title="Staff"
        description="Active and former staff. Click any row for full profile + qualifications."
        count={data ? { value: data.totalElements, label: 'matching' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/staff/new')}>
              New staff
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by name, CNIC or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          placeholder="Any department"
          allowClear
          value={departmentId}
          onChange={(v) => {
            setDepartmentId(v);
            setDesignationId(undefined);
          }}
          options={lookups.departments.map((d) => ({ value: d.id, label: d.name }))}
          style={{ minWidth: 180 }}
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Any designation"
          allowClear
          value={designationId}
          onChange={setDesignationId}
          options={lookups.designations
            .filter((d) => departmentId == null || d.departmentId === departmentId)
            .map((d) => ({ value: d.id, label: d.name }))}
          style={{ minWidth: 200 }}
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Any type"
          allowClear
          value={employmentTypeId}
          onChange={setEmploymentTypeId}
          options={lookups.employmentTypes.map((t) => ({ value: t.id, label: t.name }))}
          style={{ minWidth: 160 }}
        />
        <Select
          value={active}
          onChange={setActive}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Left only' },
          ]}
          style={{ minWidth: 140 }}
        />
      </Space>

      <CreateUserFromStaffModal
        staff={createUserFor}
        open={createUserFor != null}
        onClose={() => setCreateUserFor(null)}
      />

      {data && data.rows.length === 0 && !isFetching ? (
        <Empty description="No staff match the current filters" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data?.rows ?? []}
            pagination={{
              current: (data?.page ?? page) + 1,
              pageSize: data?.size ?? size,
              total: data?.totalElements ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
              onChange: (next, nextSize) => {
                setPage(next - 1);
                if (nextSize !== size) setSize(nextSize);
              },
            }}
            onRow={(row) => ({
              onClick: () => navigate(`/hr/staff/${row.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      )}
    </div>
  );
}
